<template>
  <div class="pos-page py-3">
    <div class="pos-layout">
      <!-- Productos -->
      <section class="pos-products card border-0 shadow-sm">
        <div class="card-body">
          <div class="pos-products-header mb-3">
            <h6 class="text-uppercase text-muted mb-0">Productos</h6>
            <div class="pos-products-toolbar">
              <input
                v-model="search"
                type="text"
                class="form-control form-control-sm pos-search"
                placeholder="Buscar producto..."
                @input="onSearch"
              />
              <div class="pos-products-filters">
                <select
                  v-if="showPriceLevelFilter"
                  v-model.number="priceLevel"
                  class="form-select form-select-sm pos-price-select"
                  @change="onPriceLevelChange"
                >
                  <option :value="1">Franquicia</option>
                  <option :value="2">Mayorista</option>
                </select>
              </div>
            </div>
          </div>

          <div v-if="loadingProducts" class="text-center py-5">
            <i class="fas fa-spinner fa-spin fa-2x text-success"></i>
          </div>

          <div v-else class="pos-grid">
            <button
              v-for="product in products"
              :key="product.id"
              type="button"
              class="pos-product-card"
              :class="'pos-product-card--' + product.stock_level"
              :disabled="!canAddProduct(product)"
              @click="addToCart(product)"
            >
              <div class="pos-product-media">
                <div class="pos-product-badges">
                  <span
                    class="pos-source-badge"
                    :class="productSourceClass(product)"
                    :title="productSourceTitle(product)"
                  >
                    {{ productSourceLabel(product) }}
                  </span>
                  <span class="pos-stock-badge">{{ stockLabel(product) }}</span>
                </div>
                <img
                  v-if="productImageUrl(product)"
                  :src="productImageUrl(product)"
                  :alt="product.name"
                  class="pos-product-img"
                  loading="lazy"
                  @error="onProductImageError($event, product)"
                />
                <div v-else class="pos-product-img-placeholder">
                  <i class="fas fa-box-open"></i>
                </div>
              </div>
              <div class="pos-product-body">
                <p class="pos-product-name">{{ product.name }}</p>
                <p class="pos-product-ref">Ref: {{ product.ref || '—' }}</p>
                <span class="pos-product-price">{{ formatCurrency(getProductPrice(product, priceLevel)) }}</span>
              </div>
            </button>
          </div>

          <div
            v-if="!loadingProducts && products.length === 0"
            class="text-center text-muted py-4"
          >
            No hay productos disponibles
          </div>

          <div class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-2 mt-3 pt-2 border-top pos-pagination-bar">
            <span class="text-sm text-muted">
              Mostrando {{ pagination.from || 0 }}-{{ pagination.to || 0 }} de {{ pagination.total || 0 }}
            </span>
            <div class="pos-pagination">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary pos-pagination-btn"
                :disabled="pagination.currentPage <= 1"
                @click="changePage(pagination.currentPage - 1)"
              >
                &lt;
              </button>
              <span class="pos-pagination-label">
                {{ pagination.currentPage }} / {{ pagination.lastPage || 1 }}
              </span>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary pos-pagination-btn"
                :disabled="pagination.currentPage >= pagination.lastPage"
                @click="changePage(pagination.currentPage + 1)"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Carrito -->
      <aside class="pos-cart card border-0 shadow-sm">
        <div class="card-body d-flex flex-column h-100">
          <h6 class="text-uppercase mb-3">Carrito de venta</h6>

          <label v-if="!isClient" class="form-control-label text-sm">Cliente</label>
          <div v-if="!isClient" class="d-flex gap-2 mb-3 pos-client-row">
            <div class="flex-grow-1 pos-client-select-wrap">
              <select ref="clientSelect" class="form-select form-select-sm select2" style="width: 100%"></select>
            </div>
            <button
              type="button"
              class="btn btn-success btn-sm px-3"
              title="Nuevo cliente"
              @click="openClientModal"
            >
              <i class="fas fa-plus"></i>
            </button>
          </div>

          <p class="text-uppercase text-sm text-muted mb-2">Productos</p>
          <div class="pos-cart-items flex-grow-1">
            <p v-if="cart.length === 0" class="text-muted text-sm">El carrito está vacío</p>
            <div
              v-for="(item, index) in cart"
              :key="cartItemKey(item)"
              class="pos-cart-card"
            >
              <div class="pos-cart-card-header">
                <div class="pos-cart-card-title">
                  <span
                    class="pos-source-badge pos-source-badge--cart"
                    :class="item.source === 'siigo' ? 'pos-source-badge--siigo' : 'pos-source-badge--local'"
                  >
                    {{ item.source === 'siigo' ? 'Siigo' : 'Local' }}
                  </span>
                  <span class="pos-cart-card-name">{{ item.name }}</span>
                </div>
                <button
                  type="button"
                  class="pos-cart-card-remove"
                  aria-label="Quitar producto"
                  @click="removeFromCart(index)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>

              <div class="pos-cart-card-qty-row">
                <div class="pos-cart-card-qty">
                  <button type="button" class="pos-qty-btn" @click="changeQty(index, -1)">−</button>
                  <input
                    type="text"
                    class="pos-qty-value"
                    inputmode="numeric"
                    :value="item.quantity"
                    @change="onQtyInput(index, $event)"
                    @focus="$event.target.select()"
                  />
                  <button type="button" class="pos-qty-btn" @click="changeQty(index, 1)">+</button>
                </div>
                <label
                  class="pos-cart-discount-check"
                  :title="discountCheckTitle"
                >
                  <input
                    v-model="item.apply_discount"
                    type="checkbox"
                    class="pos-cart-discount-input"
                    @change="onItemDiscountChange(item)"
                  />
                  <span>Desc.</span>
                </label>
              </div>
              <p v-if="item.variation_label" class="text-xs text-muted mb-2 px-1">
                Variación: {{ item.variation_label }}
              </p>

              <div class="pos-cart-card-prices">
                <div class="pos-cart-price-field">
                  <span class="pos-cart-price-label">Precio unitario</span>
                  <input
                    type="text"
                    class="form-control form-control-sm pos-cart-unit-price-input"
                    inputmode="numeric"
                    :value="unitPriceInputValue(item)"
                    :readonly="isClient"
                    @focus="onUnitPriceFocus(item)"
                    @input="onUnitPriceInput(index, item, $event)"
                    @blur="onUnitPriceBlur(index, item)"
                  />
                </div>
                <div class="text-end">
                  <span class="pos-cart-price-label">Precio total</span>
                  <span class="pos-cart-price-total">{{ formatCurrency(lineTotal(item)) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="pos-totals mt-3">
            <label class="form-control-label text-sm mb-1">Método de pago</label>
            <div class="d-flex align-items-center gap-2 mb-2">
              <select
                v-model="selectedPaymentMethod"
                class="form-select form-select-sm flex-grow-1"
                :disabled="multiPayments.length > 0"
              >
                <option v-for="method in paymentMethods" :key="method.codigo" :value="method.codigo">
                  {{ method.nombre }}
                </option>
              </select>
              <button
                type="button"
                class="btn btn-outline-primary px-2 flex-shrink-0"
                style="height: calc(1.5em + 0.5rem + 2px); font-size: 0.875rem; line-height: 1.5; padding-top: 0; padding-bottom: 0;margin-bottom:0px"
                title="Pago múltiple"
                @click="openMultiPaymentModal"
              >
                <i class="fas fa-plus"></i>
              </button>
            </div>
            <div v-if="multiPayments.length > 0" class="pos-multipay-summary mb-3">
              <div
                v-for="(mp, i) in multiPayments"
                :key="i"
                class="d-flex justify-content-between align-items-center text-xs py-1 border-bottom"
              >
                <span class="text-muted">{{ mp.method }}</span>
                <span class="fw-bold">{{ formatCurrency(mp.amount) }}</span>
              </div>
              <div class="d-flex justify-content-between text-xs pt-1">
                <span class="text-muted">Cubierto</span>
                <span :class="multiPaymentsCovered >= cartTotal ? 'text-success' : 'text-danger'">
                  {{ formatCurrency(multiPaymentsCovered) }}
                </span>
              </div>
              <button
                type="button"
                class="btn btn-xs btn-outline-secondary w-100 mt-1"
                @click="openMultiPaymentModal"
              >
                <i class="fas fa-edit me-1"></i>Editar pagos
              </button>
            </div>
            <div v-else class="mb-3"></div>

            <label class="form-control-label text-sm mb-1">Tipo de factura</label>
            <select
              v-model="selectedTipoDoc"
              class="form-select form-select-sm mb-3"
            >
              <option value="00FE">Factura de venta (00FE)</option>
              <option value="OS">Factura de venta (OS)</option>
            </select>

            <div v-if="!isClient" class="d-flex align-items-center justify-content-between mb-1">
              <label class="form-control-label text-sm mb-0">Descuento</label>
              <div class="btn-group btn-group-sm" role="group">
                <button type="button" :class="['btn btn-xs', discountMode === 'percent' ? 'btn-primary' : 'btn-outline-secondary']" @click="discountMode = 'percent'">%</button>
                <button type="button" :class="['btn btn-xs', discountMode === 'fixed' ? 'btn-primary' : 'btn-outline-secondary']" @click="discountMode = 'fixed'">$</button>
              </div>
            </div>
            <input
              v-if="!isClient"
              v-model.number="discountValue"
              type="number"
              class="form-control form-control-sm mb-3"
              min="0"
              :max="discountMode === 'percent' ? 100 : undefined"
              step="0.01"
              :placeholder="discountMode === 'percent' ? '0 %' : '0 $'"
            />

            <template v-if="!isClient">
              <label class="form-control-label text-sm mb-1">Flete</label>
              <input
                type="text"
                class="form-control form-control-sm mb-3"
                inputmode="numeric"
                :value="fleteInputValue"
                @focus="onFleteFocus"
                @input="onFleteInput"
                @blur="onFleteBlur"
              />

              <label class="form-control-label text-sm mb-1">Valor obsequio</label>
              <input
                type="text"
                class="form-control form-control-sm mb-2"
                inputmode="numeric"
                :value="giftInputValue"
                @focus="onGiftFocus"
                @input="onGiftInput"
                @blur="onGiftBlur"
              />
              <input
                v-if="giftValue > 0"
                v-model="giftDescription"
                type="text"
                class="form-control form-control-sm mb-3"
                maxlength="255"
                placeholder="Descripción del obsequio"
              />
            </template>

            <div v-if="isClient" class="mb-3">
              <label class="form-control-label text-sm mb-1">Evidencia de pago</label>
              <input
                type="file"
                class="form-control form-control-sm"
                accept="image/*"
                @change="handleEvidenceSelect"
              />
              <small class="text-muted">Sube captura evidencia de pago</small>
              <div v-if="paymentEvidencePreview" class="mt-2">
                <img :src="paymentEvidencePreview" alt="Evidencia" class="img-thumbnail" style="max-width: 120px; max-height: 120px; object-fit: cover;" />
              </div>
            </div>

            <div class="d-flex justify-content-between text-sm">
              <span>Subtotal</span>
              <span>{{ formatCurrency(cartSubtotal) }}</span>
            </div>
            <div v-if="cartDiscountAmount > 0" class="d-flex justify-content-between text-sm mt-1">
              <span v-if="discountMode === 'percent'">Descuento ({{ normalizedDiscountPercent }}%)</span>
              <span v-else>Descuento (valor fijo)</span>
              <span class="text-danger">−{{ formatCurrency(cartDiscountAmount) }}</span>
            </div>
            <div v-if="normalizedFlete > 0" class="d-flex justify-content-between text-sm mt-1">
              <span>Flete</span>
              <span class="text-info">+{{ formatCurrency(normalizedFlete) }}</span>
            </div>
            <div v-if="normalizedGiftValue > 0" class="d-flex justify-content-between text-sm mt-1">
              <span>Obsequio<span v-if="giftDescription"> ({{ giftDescription }})</span></span>
              <span class="text-success">+{{ formatCurrency(normalizedGiftValue) }}</span>
            </div>
            <div class="d-flex justify-content-between mt-2">
              <span class="font-weight-bold">TOTAL</span>
              <span class="pos-total-amount">{{ formatCurrency(cartTotal) }}</span>
            </div>

            <label class="form-control-label text-sm mb-1 mt-3">Valor pagado</label>
            <input
              type="text"
              class="form-control form-control-sm mb-2"
              inputmode="numeric"
              :value="amountPaidInputValue"
              @focus="onAmountPaidFocus"
              @input="onAmountPaidInput"
              @blur="onAmountPaidBlur"
            />
            <div v-if="paymentChange > 0" class="d-flex justify-content-between text-sm">
              <span>Cambio</span>
              <span class="text-success">{{ formatCurrency(paymentChange) }}</span>
            </div>
            <div v-else-if="paymentPending > 0" class="d-flex justify-content-between text-sm">
              <span>Pendiente</span>
              <span class="text-danger">{{ formatCurrency(paymentPending) }}</span>
            </div>
          </div>

          <button
            type="button"
            class="btn btn-success w-100 mt-3"
            :disabled="saving || cart.length === 0"
            @click="saveSale"
          >
            <i class="fas fa-save me-1"></i>
            {{ saving ? 'Guardando...' : 'Guardar' }}
          </button>

          <div v-if="!isSeller" class="mt-2 d-flex align-items-center justify-content-between gap-2">
            <span class="badge text-xs px-2 py-1" :class="cajaCurrent && cajaCurrent.id ? 'bg-success' : 'bg-danger'">
              <i class="fas me-1" :class="cajaCurrent && cajaCurrent.id ? 'fa-lock-open' : 'fa-lock'"></i>
              {{ cajaCurrent && cajaCurrent.id ? 'Caja abierta' : 'Caja cerrada' }}
            </span>
            <button
              type="button"
              class="btn btn-sm"
              :class="cajaCurrent && cajaCurrent.id ? 'btn-outline-danger' : 'btn-outline-success'"
              @click="openCajaModal"
            >
              {{ cajaCurrent && cajaCurrent.id ? 'Cerrar caja' : 'Abrir caja' }}
            </button>
          </div>
        </div>
      </aside>
    </div>

    <!-- Modal cliente -->
    <div class="modal fade" id="posClientModal" tabindex="-1" ref="clientModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Nuevo cliente</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <label class="form-control-label">Nombre <span class="text-danger">*</span></label>
            <input v-model="newClient.name" type="text" class="form-control mb-3" />
            <label class="form-control-label">Documento</label>
            <input v-model="newClient.document" type="text" class="form-control mb-3" />
            <label class="form-control-label">Correo</label>
            <input
              v-model="newClient.email"
              type="email"
              class="form-control mb-1"
              placeholder="Opcional"
            />
            <p class="text-muted text-xs mb-3">
              Si no indica correo, se usará cliente+hash@mistock.co
            </p>
            <label class="form-control-label">Teléfono</label>
            <input
              v-model="newClient.phone"
              type="tel"
              class="form-control mb-3"
              placeholder="Opcional"
            />
            <label class="form-control-label">Departamento</label>
            <select v-model="newClient.departamento" class="form-select mb-3" @change="onClientDepartamentoChange">
              <option value="">Seleccione (opcional)</option>
              <option v-for="dep in departamentos" :key="dep" :value="dep">{{ dep }}</option>
            </select>
            <label class="form-control-label">Municipio</label>
            <select v-model="newClient.municipio" class="form-select mb-3" :disabled="!newClient.departamento">
              <option value="">Seleccione (opcional)</option>
              <option v-for="mun in clientMunicipios" :key="mun" :value="mun">{{ mun }}</option>
            </select>
            <label class="form-control-label">Código Postal</label>
            <input v-model="newClient.codigo_postal" type="text" class="form-control mb-0" placeholder="Opcional" />
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button type="button" class="btn btn-success" @click="saveClient">Guardar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal apertura/cierre de caja -->
    <div class="modal fade" id="posCajaModal" tabindex="-1" ref="cajaModal" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas me-2" :class="cajaIsOpen ? 'fa-lock text-danger' : 'fa-lock-open text-success'"></i>
              {{ cajaIsOpen ? 'Cerrar caja' : 'Abrir caja' }}
            </h5>
          </div>
          <div class="modal-body">

            <!-- ABRIR CAJA -->
            <div v-if="!cajaIsOpen">
              <p class="text-sm text-muted mb-3">
                Debes abrir la caja para poder registrar ventas.
              </p>
              <div v-if="cajaLastClose" class="alert alert-info text-sm py-2">
                Último cierre: <strong>{{ formatCurrencyCaja(cajaLastClose.closing_amount) }}</strong>
                ({{ formatDateCaja(cajaLastClose.closed_at) }})
              </div>
              <label class="form-control-label">Monto de apertura</label>
              <input
                type="text"
                class="form-control mb-1"
                inputmode="numeric"
                :value="cajaOpeningFormatted"
                @focus="onCajaOpeningFocus"
                @input="onCajaOpeningInput"
                @blur="onCajaOpeningBlur"
                placeholder="0"
              />
              <small v-if="cajaLastClose" class="text-muted d-block mb-3">
                Sugerido: {{ formatCurrencyCaja(cajaLastClose.closing_amount) }}
              </small>
              <label class="form-control-label">Notas (opcional)</label>
              <textarea class="form-control" v-model="cajaOpeningNotes" rows="2" maxlength="500" placeholder="Observaciones..."></textarea>
            </div>

            <!-- CERRAR CAJA -->
            <div v-else>
              <div class="alert alert-success text-sm py-2 mb-3">
                Abierta desde: <strong>{{ formatDateCaja(cajaCurrent && cajaCurrent.opened_at) }}</strong><br/>
                Monto apertura: <strong>{{ formatCurrencyCaja(cajaCurrent && cajaCurrent.opening_amount) }}</strong>
              </div>

              <!-- Tabla de pagos por método -->
              <div v-if="closingReportLoading" class="text-center py-3">
                <i class="fas fa-spinner fa-spin text-warning"></i> Cargando reporte...
              </div>
              <div v-else-if="closingReport && closingReport.rows && closingReport.rows.length > 0" class="mb-3">
                <p class="text-uppercase text-xs font-weight-bold text-muted mb-2">Movimientos del día</p>
                <div class="table-responsive">
                  <table class="table table-sm table-bordered mb-1">
                    <thead class="table-light">
                      <tr>
                        <th class="text-xs">Método de pago</th>
                        <th class="text-xs text-end">Compras</th>
                        <th class="text-xs text-end">Ventas / Abonos</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in closingReport.rows" :key="row.method">
                        <td class="text-xs">{{ row.method }}</td>
                        <td class="text-xs text-end text-danger">{{ row.purchases > 0 ? formatCurrencyCaja(row.purchases) : '—' }}</td>
                        <td class="text-xs text-end text-success">{{ row.sales > 0 ? formatCurrencyCaja(row.sales) : '—' }}</td>
                      </tr>
                    </tbody>
                    <tfoot class="table-light fw-bold">
                      <tr>
                        <td class="text-xs">TOTAL</td>
                        <td class="text-xs text-end text-danger">{{ formatCurrencyCaja(closingReport.total_purchases) }}</td>
                        <td class="text-xs text-end text-success">{{ formatCurrencyCaja(closingReport.total_sales) }}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div class="alert py-2 mb-3" :class="closingReport.balance >= 0 ? 'alert-success' : 'alert-danger'">
                  <div class="d-flex justify-content-between align-items-center">
                    <span class="text-sm">Apertura + Ventas − Compras</span>
                    <strong class="text-sm">{{ formatCurrencyCaja(closingReport.balance) }}</strong>
                  </div>
                  <div class="text-xs text-muted mt-1">
                    {{ formatCurrencyCaja(closingReport.opening_amount) }} + {{ formatCurrencyCaja(closingReport.total_sales) }} − {{ formatCurrencyCaja(closingReport.total_purchases) }}
                  </div>
                </div>
              </div>
              <div v-else-if="!closingReportLoading" class="alert alert-warning text-xs py-2 mb-3">
                No hay movimientos registrados en esta sesión.
              </div>

              <label class="form-control-label">Monto de cierre</label>
              <input
                type="text"
                class="form-control mb-3"
                inputmode="numeric"
                :value="cajaClosingFormatted"
                @focus="onCajaClosingFocus"
                @input="onCajaClosingInput"
                @blur="onCajaClosingBlur"
                placeholder="0"
              />
              <label class="form-control-label">Notas (opcional)</label>
              <textarea class="form-control" v-model="cajaClosingNotes" rows="2" maxlength="500" placeholder="Observaciones..."></textarea>
            </div>

          </div>
          <div class="modal-footer">
            <button v-if="cajaIsOpen" type="button" class="btn btn-secondary" @click="closeCajaModal">Cancelar</button>
            <button
              type="button"
              class="btn w-100"
              :class="cajaIsOpen ? 'btn-danger' : 'btn-success'"
              :disabled="cajaSaving"
              @click="cajaIsOpen ? handleCajaClose() : handleCajaOpen()"
            >
              <i class="fas me-1" :class="cajaIsOpen ? 'fa-lock' : 'fa-lock-open'"></i>
              {{ cajaSaving ? 'Procesando...' : (cajaIsOpen ? 'Cerrar caja' : 'Abrir caja') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal pago múltiple -->
    <div class="modal fade" id="posMultiPaymentModal" tabindex="-1" ref="multiPaymentModal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fas fa-money-bill-wave me-2 text-success"></i>Pago múltiple</h5>
            <button type="button" class="btn-close" @click="closeMultiPaymentModal"></button>
          </div>
          <div class="modal-body">
            <p class="text-sm text-muted mb-3">
              Total de la venta: <strong>{{ formatCurrency(cartTotal) }}</strong>
            </p>

            <div
              v-for="(row, i) in multiPaymentDraft"
              :key="i"
              class="d-flex gap-2 align-items-center mb-2"
            >
              <select
                v-model="row.method"
                class="form-select form-select-sm"
                style="min-width: 0; flex: 1.2"
              >
                <option v-for="m in paymentMethods" :key="m.codigo" :value="m.codigo">{{ m.nombre }}</option>
              </select>
              <input
                v-model.number="row.amount"
                type="number"
                min="0"
                step="1"
                class="form-control form-control-sm"
                style="min-width: 0; flex: 1"
                placeholder="Monto"
              />
              <button
                type="button"
                class="btn btn-sm btn-outline-danger px-2"
                :disabled="multiPaymentDraft.length <= 1"
                @click="removeMultiPaymentRow(i)"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>

            <button
              type="button"
              class="btn btn-sm btn-outline-success w-100 mt-1 mb-3"
              @click="addMultiPaymentRow"
            >
              <i class="fas fa-plus me-1"></i>Agregar método
            </button>

            <div class="d-flex justify-content-between text-sm fw-bold border-top pt-2">
              <span>Total cubierto</span>
              <span :class="multiPaymentDraftCovered >= cartTotal ? 'text-success' : 'text-danger'">
                {{ formatCurrency(multiPaymentDraftCovered) }}
                <span v-if="multiPaymentDraftCovered < cartTotal" class="text-danger">
                  (faltan {{ formatCurrency(cartTotal - multiPaymentDraftCovered) }})
                </span>
              </span>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeMultiPaymentModal">Cancelar</button>
            <button
              type="button"
              class="btn btn-danger btn-sm me-auto"
              @click="clearMultiPayments"
            >
              <i class="fas fa-trash me-1"></i>Limpiar
            </button>
            <button
              type="button"
              class="btn btn-success"
              @click="saveMultiPayments"
            >
              Guardar pagos
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal variación producción -->
    <div class="modal fade" id="posVariationModal" tabindex="-1" ref="variationModal">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Seleccionar talla y color</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p v-if="pendingProduct" class="text-sm mb-3">
              Producto: <strong>{{ pendingProduct.name }}</strong>
            </p>
            <div v-if="loadingVariations" class="text-center py-4">
              <i class="fas fa-spinner fa-spin text-success"></i>
            </div>
            <div v-else-if="productionVariations.length === 0" class="text-muted text-sm">
              No hay variaciones disponibles en inventario de producción.
            </div>
            <div v-else class="list-group">
              <button
                v-for="variation in productionVariations"
                :key="variation.id"
                type="button"
                class="list-group-item list-group-item-action"
                :class="{ active: selectedVariationId === variation.id }"
                @click="selectedVariationId = variation.id"
              >
                <div class="d-flex justify-content-between align-items-start gap-2">
                  <div class="text-start">
                    <strong>Talla {{ variation.size }} · {{ variation.color_name }}</strong>
                    <span v-if="variation.color_ref" class="text-muted"> ({{ variation.color_ref }})</span>
                    <div class="text-xs text-muted mt-1">
                      {{ variation.production_name }}
                      <span v-if="variation.bodega_name"> · {{ variation.bodega_name }}</span>
                    </div>
                  </div>
                  <span class="badge bg-success">Stock: {{ Math.floor(variation.stock) }}</span>
                </div>
              </button>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button
              type="button"
              class="btn btn-success"
              :disabled="!selectedVariationId || loadingVariations"
              @click="confirmVariationSelection"
            >
              Agregar al carrito
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import posService from "@/services/pos.service";
import cashRegisterService from "@/services/cashRegister.service";
import { buildInventoryImageUrl, inventoryTicketName } from "@/utils/inventoryImage";
import Swal from "sweetalert2";
import { Modal } from "bootstrap";
import $ from "jquery";
import "select2/dist/css/select2.css";
import "select2";
import {
  savePosTicketData,
  openPosTicketWindow,
  buildInvoiceNumber,
  formatTicketDate,
  POS_PAYMENT_METHODS,
} from "@/utils/posTicketPrint";
import { playCartAddSound } from "@/utils/cartAddSound";
import { DEPARTAMENTOS, getMunicipios } from "@/utils/colombiaLocations";

export default {
  name: "Pos",
  data() {
    return {
      search: "",
      searchTimer: null,
      products: [],
      loadingProducts: false,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        from: 0,
        to: 0,
      },
      clients: [],
      selectedClientId: null,
      cart: [],
      saving: false,
      newClient: { name: "", document: "", email: "", phone: "", departamento: "", municipio: "", codigo_postal: "" },
      clientModal: null,
      departamentos: DEPARTAMENTOS,
      clientMunicipios: [],
      variationModal: null,
      pendingProduct: null,
      productionVariations: [],
      selectedVariationId: null,
      loadingVariations: false,
      priceLevel: 1,
      posSellerSettings: {
        loaded: false,
        isOperator: false,
        profitMargin: 0,
        priceLevel: null,
      },
      companySettings: {
        company_name: "",
        description: "",
        phone: "",
        address: "",
        email: "",
        logo_path: "",
        logo_path_dark: "",
      },
      focusedUnitPriceId: null,
      unitPriceDrafts: {},
      paymentMethods: POS_PAYMENT_METHODS,
      selectedPaymentMethod: POS_PAYMENT_METHODS[0].codigo,
      selectedTipoDoc: "00FE",
      discountMode: 'percent',
      discountValue: 0,
      flete: 0,
      giftValue: 0,
      giftDraft: "",
      focusedGift: false,
      giftDescription: '',
      fleteDraft: "",
      focusedFlete: false,
      amountPaid: 0,
      amountPaidManual: false,
      amountPaidDraft: "",
      focusedAmountPaid: false,
      multiPayments: [],
      multiPaymentModal: null,
      multiPaymentDraft: [],
      cajaSaving: false,
      cajaOpeningAmount: 0,
      cajaOpeningDraft: "",
      cajaFocusedOpening: false,
      cajaOpeningNotes: "",
      cajaClosingAmount: 0,
      cajaClosingDraft: "",
      cajaFocusedClosing: false,
      cajaClosingNotes: "",
      cajaModal: null,
      closingReport: null,
      closingReportLoading: false,
      paymentEvidenceFile: null,
      paymentEvidencePreview: null,
    };
  },
  computed: {
    isSeller() {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const role = String(user.role);
      return role === '7' || role === '4';
    },
    isClient() {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return String(user.role) === '4';
    },
    multiPaymentsCovered() {
      return this.multiPayments.reduce((s, mp) => s + (Number(mp.amount) || 0), 0);
    },
    multiPaymentDraftCovered() {
      return this.multiPaymentDraft.reduce((s, mp) => s + (Number(mp.amount) || 0), 0);
    },
    cajaCurrent() {
      return this.$store.getters['cashRegister/current'];
    },
    cajaIsOpen() {
      if (this.isSeller) return true;
      return !!(this.cajaCurrent && this.cajaCurrent.id);
    },
    cajaLastClose() {
      return this.$store.getters['cashRegister/lastClose'];
    },
    cajaOpeningFormatted() {
      if (this.cajaFocusedOpening && this.cajaOpeningDraft !== '') return this.cajaOpeningDraft;
      return this.cajaOpeningAmount ? this.cajaOpeningAmount.toLocaleString('es-CO') : '';
    },
    cajaClosingFormatted() {
      if (this.cajaFocusedClosing && this.cajaClosingDraft !== '') return this.cajaClosingDraft;
      return this.cajaClosingAmount ? this.cajaClosingAmount.toLocaleString('es-CO') : '';
    },
    cartSubtotal() {
      return this.cart.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
    },
    cartItemsTotal() {
      return this.cart.reduce((sum, item) => sum + this.lineTotalBeforeDiscount(item), 0);
    },
    normalizedDiscountPercent() {
      if (this.discountMode === 'percent') {
        const value = parseFloat(this.discountValue);
        if (Number.isNaN(value) || value <= 0) return 0;
        return Math.min(100, value);
      }
      const fixed = parseFloat(this.discountValue);
      if (Number.isNaN(fixed) || fixed <= 0 || !this.cartItemsTotal) return 0;
      return Math.min(100, (fixed / this.cartItemsTotal) * 100);
    },
    normalizedFlete() {
      const value = parseFloat(this.flete);
      if (Number.isNaN(value) || value < 0) {
        return 0;
      }
      return value;
    },
    normalizedGiftValue() {
      const value = parseFloat(this.giftValue);
      if (Number.isNaN(value) || value <= 0) return 0;
      return value;
    },
    giftInputValue() {
      if (this.focusedGift && this.giftDraft !== "") {
        return this.giftDraft;
      }
      return this.formatUnitPriceValue(this.giftValue);
    },
    cartDiscountAmount() {
      if (this.discountMode === 'fixed') {
        const fixed = parseFloat(this.discountValue);
        if (Number.isNaN(fixed) || fixed <= 0) return 0;
        const discountableTotal = this.cart.reduce((sum, item) => {
          if (!item.apply_discount) return sum;
          return sum + this.lineTotalBeforeDiscount(item);
        }, 0);
        if (!discountableTotal) return 0;
        return Math.min(fixed, discountableTotal);
      }
      if (!this.normalizedDiscountPercent) return 0;
      return this.cart.reduce((sum, item) => {
        if (!item.apply_discount) return sum;
        const base = this.lineTotalBeforeDiscount(item);
        return sum + base * (this.normalizedDiscountPercent / 100);
      }, 0);
    },
    cartTotal() {
      const itemsTotal = this.cart.reduce((sum, item) => sum + this.lineTotal(item), 0);
      return itemsTotal + this.normalizedFlete + this.normalizedGiftValue;
    },
    normalizedAmountPaid() {
      const value = parseFloat(this.amountPaid);
      if (Number.isNaN(value) || value < 0) {
        return 0;
      }
      return value;
    },
    paymentChange() {
      return Math.max(0, this.normalizedAmountPaid - this.cartTotal);
    },
    paymentPending() {
      return Math.max(0, this.cartTotal - this.normalizedAmountPaid);
    },
    amountPaidInputValue() {
      if (this.focusedAmountPaid && this.amountPaidDraft !== "") {
        return this.amountPaidDraft;
      }
      return this.formatUnitPriceValue(this.amountPaid);
    },
    fleteInputValue() {
      if (this.focusedFlete && this.fleteDraft !== "") {
        return this.fleteDraft;
      }
      return this.formatUnitPriceValue(this.flete);
    },
    discountCheckTitle() {
      if (this.discountMode === 'fixed' && parseFloat(this.discountValue) > 0) {
        return `Aplicar descuento de ${this.formatCurrency(this.discountValue)} a este producto`;
      }
      if (this.normalizedDiscountPercent > 0) {
        return `Aplicar descuento del ${this.normalizedDiscountPercent}% a este producto`;
      }
      return "Marque para aplicar el descuento del carrito a este producto";
    },
    cartProductSource() {
      if (!this.cart.length) {
        return null;
      }
      return this.isSiigoProduct(this.cart[0]) ? "siigo" : "local";
    },
    posUserSettings() {
      return this.posSellerSettings.loaded ? this.posSellerSettings : this.readPosSettingsFromStorage();
    },
    /** Operario con lista única: ocultar selector. Operario con todas o admin/supervisor: mostrar. */
    showPriceLevelFilter() {
      if (!this.posUserSettings?.isOperator) {
        return true;
      }
      return this.posUserSettings.priceLevel == null;
    },
  },
  watch: {
    cartTotal: {
      immediate: true,
      handler(newTotal) {
        if (!this.amountPaidManual) {
          this.amountPaid = Math.max(0, Number(newTotal) || 0);
        }
      },
    },
  },
  mounted() {
    this.init();
    this.clientModal = new Modal(this.$refs.clientModal);
    this.variationModal = new Modal(this.$refs.variationModal);
    this.cajaModal = new Modal(this.$refs.cajaModal);
    this.multiPaymentModal = new Modal(this.$refs.multiPaymentModal);
    this.initCaja();
  },
  beforeUnmount() {
    this.destroyClientSelect2();
  },
  methods: {
    async init() {
      await this.loadPosSellerSettings();
      this.applyOperatorPriceSettings();
      if (this.isClient) {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        this.selectedClientId = user.id || null;
      }
      await Promise.all([this.loadClients(), this.loadProducts(), this.loadCompanySettings()]);
    },
    readPosSettingsFromStorage() {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const isOperator = ["3", "6", "7"].includes(String(user.role));
        const assignedLevel = Number(user.pos_price_level);
        return {
          loaded: false,
          isOperator,
          profitMargin: isOperator ? Math.max(0, parseFloat(user.pos_profit_margin) || 0) : 0,
          priceLevel: isOperator && assignedLevel >= 1 && assignedLevel <= 5 ? assignedLevel : null,
        };
      } catch {
        return {
          loaded: false,
          isOperator: false,
          profitMargin: 0,
          priceLevel: null,
        };
      }
    },
    async loadPosSellerSettings() {
      try {
        const res = await posService.getSellerSettings();
        const data = res.data || {};
        this.posSellerSettings = {
          loaded: true,
          isOperator: !!data.is_operator,
          profitMargin: Math.max(0, parseFloat(data.profit_margin) || 0),
          priceLevel: data.pos_price_level ? Number(data.pos_price_level) : null,
        };

        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        if (stored.id) {
          localStorage.setItem(
            "user",
            JSON.stringify({
              ...stored,
              pos_profit_margin: data.profit_margin ?? stored.pos_profit_margin,
              pos_price_level: data.pos_price_level ?? stored.pos_price_level,
            })
          );
        }
      } catch (error) {
        console.error("No se pudo cargar configuración POS del vendedor:", error);
        this.posSellerSettings = this.readPosSettingsFromStorage();
      }
    },
    applyOperatorPriceSettings() {
      if (this.posUserSettings?.priceLevel) {
        this.priceLevel = this.posUserSettings.priceLevel;
      }
    },
    async loadCompanySettings() {
      try {
        const response = await this.$store.dispatch("settings/get");
        const data = response?.data || response || {};
        this.companySettings = {
          company_name: data.company_name || "",
          nit: data.nit || "",
          description: data.description || "",
          phone: data.phone || "",
          address: data.address || "",
          email: data.email || "",
          logo_path: data.logo_path || "",
          logo_path_dark: data.logo_path_dark || "",
        };
      } catch (e) {
        console.error("No se pudo cargar configuración de empresa:", e);
      }
    },
    buildTicketPayload(remissionId, client) {
      const items = this.cart.map((item) => ({
        name: inventoryTicketName(item),
        ref: item.ref,
        quantity: item.quantity,
        unitPrice: item.unit_price,
        total: this.lineTotal(item),
      }));
      const articlesCount = items.reduce((sum, i) => sum + Number(i.quantity || 0), 0);
      const total = this.cartTotal;
      let seller = "LOCAL";
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        seller = user?.name || seller;
      } catch {
        /* ignore */
      }

      return {
        remissionId,
        invoiceNo: buildInvoiceNumber(remissionId),
        dateFormatted: formatTicketDate(new Date()),
        client: {
          id: client.id,
          name: client.name,
          document_type: client.document_type || "CC",
          document: client.document || String(client.id),
        },
        items,
        subtotal: this.cartSubtotal,
        itemsTotal: this.cartItemsTotal,
        discountPercent: this.normalizedDiscountPercent,
        discountAmount: this.cartDiscountAmount,
        flete: this.normalizedFlete,
        giftValue: this.normalizedGiftValue,
        giftDescription: this.normalizedGiftValue > 0 ? (this.giftDescription || null) : null,
        total,
        received: this.normalizedAmountPaid,
        change: this.paymentChange,
        paymentMethod: this.multiPayments.length > 0
          ? this.multiPayments.map((mp) => `${mp.method}: ${this.formatCurrency(mp.amount)}`).join(' | ')
          : this.selectedPaymentMethod,
        paymentMethods: this.multiPayments.length > 0
          ? this.multiPayments.map((mp) => ({ method: mp.method, amount: mp.amount }))
          : null,
        articlesCount,
        itemsCount: items.length,
        seller: String(seller).toUpperCase(),
        company: { ...this.companySettings },
      };
    },
    async loadProducts(page = 1) {
      this.loadingProducts = true;
      try {
        const params = {
          page,
          per_page: 12,
          source: "all",
        };
        if (this.search) {
          params.search = this.search;
        }
        const res = await posService.getProducts(params);
        const data = res.data;
        const rows = data.data || [];
        this.products = Array.isArray(rows) ? rows.map((item) => this.mapProduct(item)) : [];
        this.pagination = {
          currentPage: data.current_page || 1,
          lastPage: data.last_page || 1,
          total: data.total || this.products.length,
          from: data.from || (this.products.length ? 1 : 0),
          to: data.to || this.products.length,
        };
      } catch (e) {
        console.error(e);
        this.products = [];
      } finally {
        this.loadingProducts = false;
      }
    },
    mapProduct(item) {
      const source = item.source === "siigo" || (item.siigo_id && String(item.siigo_id).trim() !== "")
        ? "siigo"
        : "local";
      return {
        ...item,
        source,
        siigo_id: item.siigo_id || null,
        precio1: parseFloat(item.precio1 || 0),
        precio2: parseFloat(item.precio2 || 0),
        precio3: parseFloat(item.precio3 || 0),
        precio4: parseFloat(item.precio4 || 0),
        precio5: parseFloat(item.precio5 || 0),
        price: parseFloat(item.price || 0),
        iva: parseFloat(item.iva || 0),
        descuento: parseFloat(item.descuento || 0),
        imageLoadFailed: false,
        image_url:
          buildInventoryImageUrl(item.image_path) ||
          buildInventoryImageUrl(item.image_url),
        production_stock: parseFloat(item.production_stock || 0),
        has_production_variations: !!item.has_production_variations,
      };
    },
    productImageUrl(product) {
      if (!product || product.imageLoadFailed) return null;
      if (!product.image_path && !product.image_url) return null;
      return (
        buildInventoryImageUrl(product.image_path) ||
        buildInventoryImageUrl(product.image_url)
      );
    },
    onProductImageError(_event, product) {
      if (product) {
        product.imageLoadFailed = true;
      }
    },
    async loadClients(selectId = null) {
      try {
        const res = await posService.getClients();
        this.clients = res.data || [];
        if (selectId) {
          this.selectedClientId = selectId;
        } else if (this.clients.length && !this.selectedClientId) {
          this.selectedClientId = this.clients[0].id;
        }
        await this.$nextTick();
        this.initClientSelect2(selectId);
      } catch (e) {
        console.error("Error al cargar clientes:", e);
        this.clients = [];
      }
    },
    destroyClientSelect2() {
      const select = this.$refs.clientSelect;
      if (!select) return;
      const $select = $(select);
      if ($select.hasClass("select2-hidden-accessible")) {
        $select.off("change");
        $select.select2("destroy");
      }
    },
    initClientSelect2(selectId = null) {
      const select = this.$refs.clientSelect;
      if (!select) return;

      const $select = $(select);
      this.destroyClientSelect2();

      $select.empty();
      $select.append('<option value="">Seleccionar cliente</option>');

      this.clients.forEach((client) => {
        const doc = client.document || client.id;
        const label = `${doc} - ${client.name}`;
        $select.append(new Option(label, client.id, false, false));
      });

      $select.select2({
        placeholder: "Seleccione un cliente",
        allowClear: false,
        width: "100%",
        dropdownParent: $(this.$el).find(".pos-client-select-wrap"),
      });

      $select.on("change", () => {
        const id = $select.val();
        this.selectedClientId = id ? Number(id) : null;
      });

      const idToSet = selectId ?? this.selectedClientId;
      if (idToSet) {
        $select.val(String(idToSet)).trigger("change");
      }
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(async () => {
        await this.loadProducts(1);
        const term = this.search.trim();
        if (!term) return;
        const exactMatch = this.products.find(
          (p) => p.barcode && p.barcode.trim() === term
        );
        if (exactMatch) {
          this.search = "";
          await this.addToCart(exactMatch);
        }
      }, 350);
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.lastPage) return;
      this.loadProducts(page);
    },
    getProductPrice(product, level = this.priceLevel) {
      const lvl = Number(level) || 1;
      const raw = product[`precio${lvl}`];
      if (raw === null || raw === undefined || raw === "") {
        return 0;
      }
      const selected = parseFloat(raw);
      return Number.isNaN(selected) ? 0 : selected;
    },
    stockLabel(product) {
      if (product.has_production_variations && (product.production_stock || 0) > 0) {
        return Math.floor(product.production_stock);
      }
      if (!product.manage_stock) return "∞";
      return Math.floor(product.quantity || 0);
    },
    productSource(product) {
      if (product?.source === "siigo" || product?.source === "local") {
        return product.source;
      }
      return this.isSiigoProduct(product) ? "siigo" : "local";
    },
    isSiigoProduct(product) {
      return this.productSource(product) === "siigo";
    },
    productSourceLabel(product) {
      return this.productSource(product) === "siigo" ? "Siigo" : "Local";
    },
    productSourceClass(product) {
      return this.productSource(product) === "siigo"
        ? "pos-source-badge--siigo"
        : "pos-source-badge--local";
    },
    productSourceTitle(product) {
      if (this.productSource(product) === "siigo" && product?.siigo_id) {
        return String(product.siigo_id);
      }
      return "Producto local";
    },
    productMatchesCartSource(product) {
      if (!this.cart.length) {
        return true;
      }
      const cartIsSiigo = this.cartProductSource === "siigo";
      return cartIsSiigo === this.isSiigoProduct(product);
    },
    canAddProduct(product) {
      if (!this.productMatchesCartSource(product)) {
        return false;
      }
      if (product.has_production_variations && (product.production_stock || 0) > 0) {
        return true;
      }
      if (!product.manage_stock) return true;
      return (product.quantity || 0) > 0;
    },
    cartItemKey(item) {
      if (item.production_models_id) {
        return `${item.inventory_id}-${item.production_models_id}`;
      }
      return String(item.inventory_id);
    },
    findCartItem(productId, productionModelsId = null) {
      return this.cart.find((item) => {
        if (productionModelsId) {
          return (
            String(item.inventory_id) === String(productId) &&
            String(item.production_models_id) === String(productionModelsId)
          );
        }
        return String(item.inventory_id) === String(productId) && !item.production_models_id;
      });
    },
    async addToCart(product) {
      if (!this.productMatchesCartSource(product)) {
        const cartLabel = this.cartProductSource === "siigo" ? "Siigo" : "locales";
        const productLabel = this.isSiigoProduct(product) ? "Siigo" : "local";
        Swal.fire(
          "Tipo de producto",
          `El carrito solo admite productos ${cartLabel}. Este producto es ${productLabel}.`,
          "warning"
        );
        return;
      }
      if (!this.canAddProduct(product)) {
        Swal.fire("Sin stock", "No hay unidades disponibles de este producto.", "warning");
        return;
      }

      if (product.has_production_variations && (product.production_stock || 0) > 0) {
        await this.openVariationModal(product);
        return;
      }

      this.pushToCart(product, null);
    },
    async openVariationModal(product) {
      this.pendingProduct = product;
      this.productionVariations = [];
      this.selectedVariationId = null;
      this.loadingVariations = true;
      this.variationModal.show();

      try {
        const response = await posService.getProductionVariations(product.id);
        this.productionVariations = Array.isArray(response.data) ? response.data : [];
        if (this.productionVariations.length === 0) {
          this.variationModal.hide();
          this.pushToCart(product, null);
        }
      } catch (error) {
        console.error("Error al cargar variaciones de producción:", error);
        this.variationModal.hide();
        Swal.fire(
          "Error",
          "No se pudieron cargar las variaciones de producción.",
          "error"
        );
      } finally {
        this.loadingVariations = false;
      }
    },
    confirmVariationSelection() {
      if (!this.pendingProduct || !this.selectedVariationId) {
        Swal.fire("Selección requerida", "Elija una talla y color.", "warning");
        return;
      }

      const variation = this.productionVariations.find(
        (item) => String(item.id) === String(this.selectedVariationId)
      );
      if (!variation) {
        Swal.fire("Error", "La variación seleccionada no es válida.", "error");
        return;
      }

      this.variationModal.hide();
      this.pushToCart(this.pendingProduct, variation);
      this.pendingProduct = null;
      this.productionVariations = [];
      this.selectedVariationId = null;
    },
    pushToCart(product, variation) {
      const productionModelsId = variation?.id || null;
      const existing = this.findCartItem(product.id, productionModelsId);
      const manageStock = variation ? true : product.manage_stock;
      const maxStock = variation ? variation.stock : product.quantity;

      if (existing) {
        if (manageStock && existing.quantity >= maxStock) {
          Swal.fire("Stock máximo", "No puede agregar más unidades.", "warning");
          return;
        }
        existing.quantity += 1;
        if (!existing.unit_price_manual) {
          existing.price_level = this.priceLevel;
          this.updateCartPrice(existing);
        }
        playCartAddSound();
        return;
      }

      const variationLabel = variation
        ? `Talla ${variation.size} · ${variation.color_name}`
        : null;
      const displayName = variationLabel ? `${product.name} (${variationLabel})` : product.name;

      this.cart.push({
        inventory_id: product.id,
        production_models_id: productionModelsId,
        production_id: variation?.production_id || null,
        model_id: variation?.model_id || null,
        variation_label: variationLabel,
        name: displayName,
        invoice_name: product.invoice_name || null,
        ref: product.ref,
        siigo_id: product.siigo_id || null,
        source: this.productSource(product),
        quantity: 1,
        unit_price: this.getProductPrice(product),
        unit_price_manual: false,
        price_level: this.priceLevel,
        iva: parseFloat(product.iva || 0),
        descuento: parseFloat(product.descuento || 0),
        apply_discount: false,
        manage_stock: manageStock,
        max_stock: maxStock,
        precio1: product.precio1,
        precio2: product.precio2,
        precio3: product.precio3,
        precio4: product.precio4,
        precio5: product.precio5,
        price: product.price,
      });

      playCartAddSound();
    },
    onPriceLevelChange() {
      this.priceLevel = Number(this.priceLevel) || 1;
      this.cart.forEach((item) => {
        if (item.unit_price_manual) {
          return;
        }
        item.price_level = this.priceLevel;
        this.updateCartPrice(item);
      });
    },
    formatUnitPriceValue(value) {
      return new Intl.NumberFormat("es-CO", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Math.max(0, Number(value) || 0));
    },
    parseUnitPriceValue(raw) {
      const digits = String(raw ?? "").replace(/\D/g, "");
      if (digits === "") {
        return 0;
      }
      return Math.max(0, parseInt(digits, 10) || 0);
    },
    getUnitPriceCursorAfterDigits(formatted, digitCount) {
      if (digitCount <= 0) {
        return 0;
      }
      let seen = 0;
      for (let i = 0; i < formatted.length; i += 1) {
        if (/\d/.test(formatted[i])) {
          seen += 1;
          if (seen >= digitCount) {
            return i + 1;
          }
        }
      }
      return formatted.length;
    },
    unitPriceInputValue(item) {
      const key = this.cartItemKey(item);
      if (
        this.focusedUnitPriceId === key &&
        this.unitPriceDrafts[key] !== undefined
      ) {
        return this.unitPriceDrafts[key];
      }
      return this.formatUnitPriceValue(item.unit_price);
    },
    onUnitPriceFocus(item) {
      const key = this.cartItemKey(item);
      this.focusedUnitPriceId = key;
      this.unitPriceDrafts[key] = this.formatUnitPriceValue(item.unit_price);
    },
    onUnitPriceInput(index, item, event) {
      const input = event.target;
      const raw = input.value;
      const key = this.cartItemKey(item);
      const digitsBeforeCursor = raw.slice(0, input.selectionStart).replace(/\D/g, "").length;
      const digits = raw.replace(/\D/g, "");

      item.unit_price_manual = true;

      if (digits === "") {
        item.unit_price = 0;
        this.unitPriceDrafts[key] = "";
        return;
      }

      const numeric = Math.max(0, parseInt(digits, 10) || 0);
      item.unit_price = numeric;
      const formatted = this.formatUnitPriceValue(numeric);
      this.unitPriceDrafts[key] = formatted;

      this.$nextTick(() => {
        const cursor = this.getUnitPriceCursorAfterDigits(formatted, digitsBeforeCursor);
        input.setSelectionRange(cursor, cursor);
      });
    },
    onUnitPriceBlur(index, item) {
      const key = this.cartItemKey(item);
      const parsed = this.parseUnitPriceValue(this.unitPriceDrafts[key] ?? item.unit_price);
      item.unit_price = parsed;
      item.unit_price_manual = true;
      delete this.unitPriceDrafts[key];
      this.focusedUnitPriceId = null;
    },
    updateCartPrice(item) {
      item.unit_price = this.getProductPrice(item, item.price_level);
    },
    onQtyInput(index, event) {
      const item = this.cart[index];
      let val = parseInt(event.target.value, 10);
      if (isNaN(val) || val < 1) {
        val = 1;
      }
      if (item.manage_stock && val > item.max_stock) {
        val = item.max_stock;
        Swal.fire("Stock máximo", "No hay más unidades disponibles.", "warning");
      }
      item.quantity = val;
      event.target.value = val;
    },
    changeQty(index, delta) {
      const item = this.cart[index];
      const next = item.quantity + delta;
      if (next < 1) {
        this.removeFromCart(index);
        return;
      }
      if (item.manage_stock && next > item.max_stock) {
        Swal.fire("Stock máximo", "No hay más unidades disponibles.", "warning");
        return;
      }
      item.quantity = next;
    },
    removeFromCart(index) {
      this.cart.splice(index, 1);
    },
    lineTotalBeforeDiscount(item) {
      // Los precios (precio1..precio5) ya incluyen IVA (provienen de
      // PrecioIva en Kubapp), por lo que NO se debe sumar IVA otra vez.
      return item.quantity * item.unit_price;
    },
    lineTotal(item) {
      const base = this.lineTotalBeforeDiscount(item);
      if (!item.apply_discount) return base;
      if (this.discountMode === 'fixed') {
        const fixed = parseFloat(this.discountValue);
        if (Number.isNaN(fixed) || fixed <= 0) return base;
        const discountableTotal = this.cart.reduce((sum, i) => {
          if (!i.apply_discount) return sum;
          return sum + this.lineTotalBeforeDiscount(i);
        }, 0);
        if (!discountableTotal) return base;
        const ratio = base / discountableTotal;
        return Math.max(0, base - fixed * ratio);
      }
      if (this.normalizedDiscountPercent > 0) {
        return base * (1 - this.normalizedDiscountPercent / 100);
      }
      return base;
    },
    onItemDiscountChange(item) {
      if (!item.apply_discount) return;
      let effectivePct = 0;
      if (this.discountMode === 'percent') {
        effectivePct = this.normalizedDiscountPercent;
      } else {
        const fixed = parseFloat(this.discountValue);
        const itemBase = this.lineTotalBeforeDiscount(item);
        if (fixed > 0 && itemBase > 0) {
          effectivePct = (fixed / itemBase) * 100;
        }
      }
      if (effectivePct > 30) {
        item.apply_discount = false;
        this.showDiscountToast(effectivePct);
      }
    },
    showDiscountToast(pct) {
      const toast = document.createElement("div");
      toast.textContent = `El descuento (${pct.toFixed(1)}%) no se puede aplicar porque supera el 30%`;
      Object.assign(toast.style, {
        position: "fixed", top: "1.2rem", right: "1.2rem", zIndex: "9999",
        background: "#dc3545", color: "#fff", padding: "0.75rem 1.2rem",
        borderRadius: "0.5rem", fontSize: "0.875rem", boxShadow: "0 4px 12px rgba(0,0,0,.25)",
        maxWidth: "320px", lineHeight: "1.4",
      });
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 4000);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 2,
      }).format(value || 0);
    },
    openMultiPaymentModal() {
      this.multiPaymentDraft = this.multiPayments.length > 0
        ? this.multiPayments.map((mp) => ({ ...mp }))
        : [{ method: this.selectedPaymentMethod, amount: this.cartTotal }];
      this.multiPaymentModal.show();
    },
    closeMultiPaymentModal() {
      this.multiPaymentModal.hide();
    },
    addMultiPaymentRow() {
      this.multiPaymentDraft.push({ method: this.paymentMethods[0].codigo, amount: 0 });
    },
    removeMultiPaymentRow(i) {
      if (this.multiPaymentDraft.length > 1) {
        this.multiPaymentDraft.splice(i, 1);
      }
    },
    saveMultiPayments() {
      const rows = this.multiPaymentDraft.filter((r) => r.method && (Number(r.amount) || 0) > 0);
      if (rows.length === 0) {
        Swal.fire('Datos incompletos', 'Ingresa al menos un método de pago con monto mayor a 0.', 'warning');
        return;
      }
      this.multiPayments = rows.map((r) => ({ method: r.method, amount: Number(r.amount) || 0 }));
      this.multiPaymentModal.hide();
    },
    clearMultiPayments() {
      this.multiPayments = [];
      this.multiPaymentDraft = [];
      this.multiPaymentModal.hide();
    },
    openClientModal() {
      this.newClient = { name: "", document: "", email: "", phone: "", departamento: "", municipio: "", codigo_postal: "" };
      this.clientMunicipios = [];
      this.clientModal.show();
    },
    onClientDepartamentoChange() {
      this.newClient.municipio = '';
      this.clientMunicipios = getMunicipios(this.newClient.departamento);
    },
    async saveClient() {
      if (!this.newClient.name.trim()) {
        Swal.fire("Nombre requerido", "Indique el nombre del cliente.", "warning");
        return;
      }
      try {
        const payload = {
          name: this.newClient.name.trim(),
          document: this.newClient.document?.trim() || null,
          email: this.newClient.email?.trim() || null,
          phone: this.newClient.phone?.trim() || null,
          departamento: this.newClient.departamento || null,
          municipio: this.newClient.municipio || null,
          codigo_postal: this.newClient.codigo_postal?.trim() || null,
        };
        const res = await posService.createClient(payload);
        await this.loadClients(res.data.id);
        this.clientModal.hide();
        Swal.fire(
          "Cliente creado",
          res.data.email ? `Correo: ${res.data.email}` : "",
          "success"
        );
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          (e?.response?.data?.errors
            ? Object.values(e.response.data.errors).flat().join(" ")
            : null) ||
          "No se pudo guardar el cliente.";
        Swal.fire("Error", msg, "error");
      }
    },
    buildRemissionForm(client) {
      const doc = client?.document ? String(client.document) : String(client?.id || 0);
      return {
        id: 0,
        user_id: client?.id || null,
        client_id: client?.id || null,
        name: client?.name || "Cliente general",
        document_type: client?.document_type || "CC",
        document: doc,
        address: client?.address || "N/A",
        phone: client?.phone || "0000000000",
        email: client?.email || `cliente${client?.id || 0}@mistock.co`,
        observation: "Venta POS",
        status: 'pendiente',
        type: "pos",
        tipo_doc_id: this.selectedTipoDoc,
        payment_method: this.multiPayments.length > 0
          ? this.multiPayments.map((mp) => mp.method).join(' / ')
          : this.selectedPaymentMethod,
        payment_methods: this.multiPayments.length > 0
          ? this.multiPayments.map((mp) => ({ method: mp.method, amount: mp.amount }))
          : null,
        discount_percent: this.normalizedDiscountPercent,
        flete: this.normalizedFlete,
        gift_value: this.normalizedGiftValue || null,
        gift_description: this.normalizedGiftValue > 0 ? (this.giftDescription || null) : null,
        amount_paid: this.multiPayments.length > 0
          ? this.multiPaymentsCovered
          : this.normalizedAmountPaid,
      };
    },
    onAmountPaidFocus() {
      this.focusedAmountPaid = true;
      this.amountPaidDraft = this.formatUnitPriceValue(this.amountPaid);
    },
    onAmountPaidInput(event) {
      const input = event.target;
      const raw = input.value;
      const digitsBeforeCursor = raw.slice(0, input.selectionStart).replace(/\D/g, "").length;
      const digits = raw.replace(/\D/g, "");

      this.amountPaidManual = true;

      if (digits === "") {
        this.amountPaid = 0;
        this.amountPaidDraft = "";
        return;
      }

      const numeric = Math.max(0, parseInt(digits, 10) || 0);
      this.amountPaid = numeric;
      const formatted = this.formatUnitPriceValue(numeric);
      this.amountPaidDraft = formatted;

      this.$nextTick(() => {
        const cursor = this.getUnitPriceCursorAfterDigits(formatted, digitsBeforeCursor);
        input.setSelectionRange(cursor, cursor);
      });
    },
    onAmountPaidBlur() {
      const parsed = this.parseUnitPriceValue(this.amountPaidDraft ?? this.amountPaid);
      this.amountPaid = parsed;
      this.amountPaidDraft = "";
      this.focusedAmountPaid = false;
      if (parsed === 0 || parsed === Math.round(this.cartTotal)) {
        this.amountPaidManual = false;
        this.amountPaid = Math.max(0, Number(this.cartTotal) || 0);
      }
    },
    onFleteFocus() {
      this.focusedFlete = true;
      this.fleteDraft = this.formatUnitPriceValue(this.flete);
    },
    onFleteInput(event) {
      const input = event.target;
      const raw = input.value;
      const digitsBeforeCursor = raw.slice(0, input.selectionStart).replace(/\D/g, "").length;
      const digits = raw.replace(/\D/g, "");

      if (digits === "") {
        this.flete = 0;
        this.fleteDraft = "";
        return;
      }

      const numeric = Math.max(0, parseInt(digits, 10) || 0);
      this.flete = numeric;
      const formatted = this.formatUnitPriceValue(numeric);
      this.fleteDraft = formatted;

      this.$nextTick(() => {
        const cursor = this.getUnitPriceCursorAfterDigits(formatted, digitsBeforeCursor);
        input.setSelectionRange(cursor, cursor);
      });
    },
    onFleteBlur() {
      const parsed = this.parseUnitPriceValue(this.fleteDraft ?? this.flete);
      this.flete = parsed;
      this.fleteDraft = "";
      this.focusedFlete = false;
    },
    onGiftFocus() {
      this.focusedGift = true;
      this.giftDraft = this.formatUnitPriceValue(this.giftValue);
    },
    onGiftInput(event) {
      const input = event.target;
      const raw = input.value;
      const digitsBeforeCursor = raw.slice(0, input.selectionStart).replace(/\D/g, "").length;
      const digits = raw.replace(/\D/g, "");

      if (digits === "") {
        this.giftValue = 0;
        this.giftDraft = "";
        return;
      }

      const numeric = Math.max(0, parseInt(digits, 10) || 0);
      this.giftValue = numeric;
      const formatted = this.formatUnitPriceValue(numeric);
      this.giftDraft = formatted;

      this.$nextTick(() => {
        const cursor = this.getUnitPriceCursorAfterDigits(formatted, digitsBeforeCursor);
        input.setSelectionRange(cursor, cursor);
      });
    },
    onGiftBlur() {
      const parsed = this.parseUnitPriceValue(this.giftDraft ?? this.giftValue);
      this.giftValue = parsed;
      this.giftDraft = "";
      this.focusedGift = false;
    },
    async saveSale() {
      if (!this.selectedClientId) {
        Swal.fire("Cliente requerido", "Seleccione un cliente para la venta.", "warning");
        return;
      }
      this.saving = true;
      try {
        const client = this.clients.find((c) => c.id == this.selectedClientId);
        if (!client) {
          throw new Error("Cliente no encontrado.");
        }

        const remissionResponse = await this.$store.dispatch(
          "remissions/save",
          this.buildRemissionForm(client)
        );

        if (!remissionResponse || remissionResponse.status < 200 || remissionResponse.status >= 300) {
          throw new Error(remissionResponse?.data?.message || "No se pudo guardar la venta.");
        }

        const remissionId = remissionResponse.data?.id;
        if (!remissionId) {
          throw new Error("No se obtuvo el identificador de la venta.");
        }

        for (const item of this.cart) {
          const detailPayload = {
            id: 0,
            remission_id: remissionId,
            quantity: item.quantity,
            price: item.unit_price,
          };

          if (item.production_models_id) {
            detailPayload.inventory_id = item.inventory_id;
            detailPayload.production_models_id = item.production_models_id;
          } else {
            detailPayload.inventory_id = item.inventory_id;
          }

          const detailResponse = await this.$store.dispatch("remissionsDetails/save", detailPayload);

          if (!detailResponse || detailResponse.status < 200 || detailResponse.status >= 300) {
            throw new Error(
              detailResponse?.data?.message ||
                `No se pudo guardar el detalle de ${item.name}.`
            );
          }
        }

        const formalizeResponse = await this.$store.dispatch("remissions/formalize", remissionId);
        if (!formalizeResponse || formalizeResponse.status < 200 || formalizeResponse.status >= 300) {
          throw new Error(
            formalizeResponse?.data?.message || "La venta se guardó pero no se pudo formalizar."
          );
        }

        await this.loadCompanySettings();
        const ticketPayload = this.buildTicketPayload(remissionId, client);
        savePosTicketData(ticketPayload);

        if (this.isClient && this.paymentEvidenceFile) {
          await this.uploadPaymentEvidence(remissionId);
        }

        this.cart = [];
        this.discountMode = 'percent';
        this.discountValue = 0;
        this.flete = 0;
        this.giftValue = 0;
        this.giftDraft = "";
        this.focusedGift = false;
        this.giftDescription = '';
        this.amountPaid = 0;
        this.amountPaidManual = false;
        this.multiPayments = [];
        this.multiPaymentDraft = [];
        this.paymentEvidenceFile = null;
        this.paymentEvidencePreview = null;
        await this.loadProducts(this.pagination.currentPage);
        await Swal.fire(
          "Venta registrada",
          `Venta #${remissionId} guardada en Ventas correctamente.`,
          "success"
        );
        openPosTicketWindow(this.$router, remissionId);
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.data?.message ||
          e?.message ||
          "No se pudo guardar la venta.";
        Swal.fire("Error", msg, "error");
      } finally {
        this.saving = false;
      }
    },
    handleEvidenceSelect(event) {
      const file = event.target.files[0];
      if (file) {
        if (!file.type.startsWith('image/')) {
          Swal.fire({
            icon: 'warning',
            title: 'Archivo inválido',
            text: 'Solo se permiten archivos de imagen (JPG, PNG, etc.)',
          });
          event.target.value = '';
          return;
        }
        this.paymentEvidenceFile = file;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.paymentEvidencePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    async uploadPaymentEvidence(remissionId) {
      if (!this.paymentEvidenceFile || !remissionId) return;
      try {
        const formData = new FormData();
        formData.append('image', this.paymentEvidenceFile);
        formData.append('remission_id', remissionId);
        await this.$store.dispatch('remissionPaymentEvidences/save', formData);
      } catch (error) {
        console.error('Error al subir evidencia de pago:', error);
        Swal.fire({
          icon: 'warning',
          title: 'Evidencia no subida',
          text: 'La venta se guardó pero no se pudo subir la evidencia de pago.',
        });
      }
    },
    async initCaja() {
      if (this.isSeller) return;
      await Promise.all([
        this.$store.dispatch('cashRegister/fetchCurrent'),
        this.$store.dispatch('cashRegister/fetchLastClose'),
      ]);
      if (this.cajaLastClose) {
        this.cajaOpeningAmount = parseFloat(this.cajaLastClose.closing_amount) || 0;
      }
      if (!(this.cajaCurrent && this.cajaCurrent.id)) {
        this.$nextTick(() => this.cajaModal && this.cajaModal.show());
      }
    },
    openCajaModal() {
      if (this.cajaIsOpen) {
        this.loadClosingReport();
      }
      this.cajaModal && this.cajaModal.show();
    },
    async loadClosingReport() {
      this.closingReportLoading = true;
      try {
        const response = await cashRegisterService.getClosingReport();
        this.closingReport = response.data;
        if (this.closingReport && this.closingReport.balance != null) {
          this.cajaClosingAmount = Math.round(this.closingReport.balance);
        }
      } catch {
        this.closingReport = null;
      } finally {
        this.closingReportLoading = false;
      }
    },
    closeCajaModal() {
      if (this.cajaIsOpen) {
        this.cajaModal && this.cajaModal.hide();
      }
    },
    onCajaOpeningFocus() {
      this.cajaFocusedOpening = true;
      this.cajaOpeningDraft = this.cajaOpeningAmount ? this.cajaOpeningAmount.toLocaleString('es-CO') : '';
    },
    onCajaOpeningInput(e) {
      const digits = e.target.value.replace(/\D/g, '');
      const num = parseInt(digits, 10) || 0;
      this.cajaOpeningAmount = num;
      this.cajaOpeningDraft = num ? num.toLocaleString('es-CO') : '';
    },
    onCajaOpeningBlur() {
      this.cajaFocusedOpening = false;
      this.cajaOpeningDraft = '';
    },
    onCajaClosingFocus() {
      this.cajaFocusedClosing = true;
      this.cajaClosingDraft = this.cajaClosingAmount ? this.cajaClosingAmount.toLocaleString('es-CO') : '';
    },
    onCajaClosingInput(e) {
      const digits = e.target.value.replace(/\D/g, '');
      const num = parseInt(digits, 10) || 0;
      this.cajaClosingAmount = num;
      this.cajaClosingDraft = num ? num.toLocaleString('es-CO') : '';
    },
    onCajaClosingBlur() {
      this.cajaFocusedClosing = false;
      this.cajaClosingDraft = '';
    },
    formatCurrencyCaja(value) {
      return (Number(value) || 0).toLocaleString('es-CO', { minimumFractionDigits: 0 });
    },
    formatDateCaja(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
    },
    async handleCajaOpen() {
      this.cajaSaving = true;
      try {
        await this.$store.dispatch('cashRegister/open', {
          opening_amount: this.cajaOpeningAmount,
          opening_notes: this.cajaOpeningNotes || null,
        });
        this.cajaOpeningNotes = '';
        this.cajaModal && this.cajaModal.hide();
        Swal.fire({ icon: 'success', title: 'Caja abierta', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire('Error', e?.response?.data?.message || 'No se pudo abrir la caja.', 'error');
      } finally {
        this.cajaSaving = false;
      }
    },
    async handleCajaClose() {
      const result = await Swal.fire({
        icon: 'warning',
        title: '¿Cerrar caja?',
        html: `Monto de cierre: <strong>${this.formatCurrencyCaja(this.cajaClosingAmount)}</strong>`,
        showCancelButton: true,
        confirmButtonText: 'Sí, cerrar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#f5365c',
      });
      if (!result.isConfirmed) return;
      this.cajaSaving = true;
      try {
        await this.$store.dispatch('cashRegister/close', {
          closing_amount: this.cajaClosingAmount,
          closing_notes: this.cajaClosingNotes || null,
        });
        await this.$store.dispatch('cashRegister/fetchLastClose');
        this.cajaClosingAmount = 0;
        this.cajaClosingNotes = '';
        this.cajaModal && this.cajaModal.hide();
        Swal.fire({ icon: 'success', title: 'Caja cerrada', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire('Error', e?.response?.data?.message || 'No se pudo cerrar la caja.', 'error');
      } finally {
        this.cajaSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.pos-page {
  min-height: calc(100vh - 120px);
}

.pos-layout {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 1rem;
  align-items: start;
}

@media (max-width: 992px) {
  .pos-layout {
    grid-template-columns: 1fr;
  }

  .pos-cart {
    position: static;
    min-height: auto;
  }
}

.pos-products-header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

@media (min-width: 768px) {
  .pos-products-header {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
}

.pos-products-toolbar {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

@media (min-width: 576px) {
  .pos-products-toolbar {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    width: auto;
    flex: 1;
    min-width: 0;
  }
}

.pos-products-filters {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  width: 100%;
}

@media (min-width: 576px) {
  .pos-products-filters {
    display: flex;
    width: auto;
    flex-shrink: 0;
  }
}

.pos-search {
  width: 100%;
  max-width: none;
  min-width: 0;
  border-radius: 999px;
  border-color: rgba(18, 18, 18, 0.2);
}

.pos-search:focus {
  border-color: #121212;
  box-shadow: none;
}

@media (min-width: 576px) {
  .pos-search {
    flex: 1;
    min-width: 160px;
    max-width: 280px;
  }
}

.pos-price-select {
  width: 100%;
  min-width: 0;
}

@media (min-width: 576px) {
  .pos-price-select {
    width: auto;
    min-width: 7rem;
  }
}

.pos-pagination-bar {
  font-size: 0.875rem;
}

@media (max-width: 575px) {
  .pos-page {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }

  .pos-products .card-body,
  .pos-cart .card-body {
    padding: 0.75rem;
  }

  .pos-pagination-bar {
    text-align: center;
  }

  .pos-pagination-bar .pos-pagination {
    justify-content: center;
    width: 100%;
  }
}

.pos-pagination {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 31px;
}

.pos-pagination-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-sizing: border-box;
  width: auto !important;
  height: auto !important;
  min-height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  background: none !important;
  box-shadow: none !important;
  color: #121212 !important;
  font-size: 0.875rem;
  line-height: 1 !important;
  text-decoration: underline;
  text-underline-offset: 3px;
  flex-shrink: 0;
}

.pos-pagination-btn:hover:not(:disabled) {
  color: rgba(18, 18, 18, 0.65) !important;
}

.pos-pagination-btn:disabled {
  color: rgba(18, 18, 18, 0.25) !important;
  text-decoration: none;
}

.pos-pagination-label {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-sizing: border-box;
  height: auto !important;
  min-height: 0 !important;
  padding: 0 0.35rem !important;
  margin: 0 !important;
  line-height: 1 !important;
  font-size: 0.875rem;
  color: rgba(18, 18, 18, 0.55);
  flex-shrink: 0;
}

.pos-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.6rem 1rem;
}

@media (max-width: 1400px) {
  .pos-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .pos-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem 0.75rem;
  }

  .pos-product-img-placeholder {
    font-size: 1.5rem;
  }

  .pos-product-body {
    padding: 0.55rem 0.15rem 0;
  }

  .pos-product-name {
    font-size: 0.68rem;
  }

  .pos-product-ref {
    font-size: 0.56rem;
    margin-bottom: 0.3rem;
  }

  .pos-source-badge {
    font-size: 0.55rem;
    padding: 0.22rem 0.45rem;
  }

  .pos-stock-badge {
    font-size: 0.55rem;
    padding: 0.22rem 0.45rem;
  }

  .pos-product-price {
    font-size: 0.78rem;
  }
}

@media (max-width: 400px) {
  .pos-grid {
    grid-template-columns: 1fr;
  }
}

.pos-product-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0;
  min-width: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  cursor: pointer;
  overflow: visible;
  text-align: left;
  box-shadow: none;
  transition: none;
}

.pos-product-card:hover:not(:disabled) {
  transform: none;
  box-shadow: none;
}

.pos-product-card:focus-visible {
  outline: 2px solid #121212;
  outline-offset: 3px;
  border-radius: 12px;
}

.pos-product-card:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pos-product-media {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #f4f5f7;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pos-product-card:hover:not(:disabled) .pos-product-img {
  transform: scale(1.04);
}

.pos-product-badges {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  pointer-events: none;
}

.pos-source-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.62rem;
  font-weight: 600;
  line-height: 1.1;
  padding: 0.28rem 0.55rem;
  border-radius: 4px;
  color: #121212;
  background: #fff;
  border: 1px solid rgba(18, 18, 18, 0.12);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.pos-source-badge--siigo {
  background: #fff;
  color: #121212;
}

.pos-source-badge--local {
  background: #121212;
  color: #fff;
  border-color: #121212;
}

.pos-source-badge--cart {
  position: static;
  display: inline-block;
  margin-right: 0.35rem;
  vertical-align: middle;
  font-size: 0.6rem;
  padding: 2px 6px;
}

.pos-cart-card-title {
  display: flex;
  align-items: flex-start;
  gap: 0;
  min-width: 0;
  flex: 1;
}

.pos-cart-card-title .pos-cart-card-name {
  flex: 1;
  min-width: 0;
}

.pos-stock-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.28rem 0.55rem;
  border-radius: 4px;
  background: #e7f4ea;
  color: #1e7a3f;
  border: 1px solid rgba(30, 122, 63, 0.18);
  font-size: 0.62rem;
  font-weight: 600;
  line-height: 1.1;
  white-space: nowrap;
}

.pos-product-card--medium .pos-stock-badge {
  background: #fdf3e2;
  color: #96660f;
  border-color: rgba(150, 102, 15, 0.2);
}

.pos-product-card--low .pos-stock-badge {
  background: #fdeaea;
  color: #b3261e;
  border-color: rgba(179, 38, 30, 0.2);
}

.pos-product-img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  transition: transform 0.35s ease;
}

.pos-product-img-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: rgba(18, 18, 18, 0.25);
  font-size: 2rem;
}

.pos-product-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 0.7rem 0.2rem 0;
}

.pos-product-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #121212;
  text-align: left;
  margin: 0 0 0.2rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pos-product-ref {
  font-size: 0.64rem;
  color: rgba(18, 18, 18, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 0.35rem;
}

.pos-product-price {
  display: inline-block;
  margin-top: auto;
  background: transparent;
  color: #121212;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 0;
  border-radius: 0;
  white-space: nowrap;
}

.pos-client-row {
  align-items: stretch;
}

.pos-client-select-wrap {
  min-width: 0;
}

.pos-client-select-wrap :deep(.select2-container) {
  width: 100% !important;
}

.pos-client-select-wrap :deep(.select2-selection--single) {
  min-height: 31px;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}

.pos-client-select-wrap :deep(.select2-selection__rendered) {
  line-height: 29px;
  padding-left: 0.5rem;
}

.pos-cart {
  min-height: 520px;
  position: sticky;
  top: 1rem;
}

@media (max-width: 992px) {
  .pos-cart-items {
    max-height: 240px;
  }
}

.pos-cart-items {
  overflow-y: auto;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.pos-cart-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  padding: 0.75rem 0.85rem;
}

.pos-cart-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.65rem;
}

.pos-cart-card-name {
  font-size: 0.8rem;
  font-weight: 700;
  color: #344767;
  text-transform: uppercase;
  line-height: 1.25;
}

.pos-cart-card-remove {
  border: none;
  background: transparent;
  color: #ea0606;
  padding: 0;
  line-height: 1;
  font-size: 0.85rem;
  cursor: pointer;
}

.pos-cart-card-qty-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.75rem;
}

.pos-cart-discount-check {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding: 0.2rem 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #344767;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #fff;
}

.pos-cart-discount-input {
  width: 1.05rem;
  height: 1.05rem;
  min-width: 1.05rem;
  margin: 0;
  cursor: pointer;
  accent-color: #82d616;
  flex-shrink: 0;
  opacity: 1;
  appearance: auto;
  -webkit-appearance: checkbox;
}

.pos-cart-card-qty {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.pos-qty-btn {
  border: none;
  background: #fff;
  color: #344767;
  width: 2rem;
  font-size: 1rem;
  line-height: 1;
  padding: 0.35rem 0;
  cursor: pointer;
}

.pos-qty-btn:not(:last-child) {
  border-right: 1px solid #dee2e6;
}

.pos-qty-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.25rem;
  width: 4.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #344767;
  border: none;
  border-right: 1px solid #dee2e6;
  background: #fff;
  text-align: center;
  padding: 0;
  outline: none;
}

.pos-cart-card-prices {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
}

.pos-cart-price-field {
  flex: 1;
  min-width: 0;
}

.pos-cart-unit-price-input {
  max-width: 9.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #344767;
  border-color: #dee2e6;
  padding: 0.25rem 0.45rem;
  height: calc(1.5em + 0.5rem + 2px);
  -moz-appearance: textfield;
}

.pos-cart-unit-price-input::-webkit-outer-spin-button,
.pos-cart-unit-price-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.pos-cart-unit-price-input:focus {
  border-color: #82d616;
  box-shadow: 0 0 0 0.15rem rgba(130, 214, 22, 0.15);
}

.pos-cart-price-label {
  display: block;
  font-size: 0.7rem;
  color: #8392ab;
  margin-bottom: 0.15rem;
}

.pos-cart-price-value {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #344767;
}

.pos-cart-price-total {
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  color: #82d616;
}

.pos-totals {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.pos-total-amount {
  color: #82d616;
  font-size: 1.25rem;
  font-weight: 700;
}

.btn-xs {
  padding: 0.1rem 0.45rem;
  font-size: 0.75rem;
}

.pos-multipay-summary {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
}
</style>
